import {
  Box,
  Button,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
} from "@mui/material";
import "../../css/App.scss";
import "./home.scss";
import { useContext, useEffect, useState } from "react";
import { SplashContext } from "../layout/layout";
import { Swiper, SwiperSlide } from "swiper/react";
import SwiperCore, { Navigation, Pagination, Autoplay } from "swiper";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import MovieList from "./components/movieList";
import CinemaList from "./components/cinemaList";
import { filterMovies } from "./components/filterMovies";
import { Link } from "react-router-dom";
import getCinemas from "./utilities/getCinemas";
import getMovies from "./utilities/getMovies";
import { useDispatch } from "react-redux";
import { setMovies } from "../../redux/slices/moviesSlice";
import { setCinemas } from "../../redux/slices/cinemasSlice";
import logo from "../../images/png/logo.png";
import NoMovies from "./components/noMovies";
// import fastX from "../../images/jpg/fast-2x.jpeg";
// import johnWick from "../../images/jpg/john-wick.jpg";
// import mario from "../../images/jpg/mario.jpg";

const Home = () => {
  /* #region Redux */
  const [cinemas, _setCinemas] = useState([]);
  const [filteredMovieList, setFilteredMovieList] = useState([]);
  const dispatch = useDispatch();
  /* #endregion */

  /* #region state  */
  const [nowShowing, setNowShowing] = useState(true);
  const [tabIndex, setTabIndex] = useState(0);
  const [experience, setExperience] = useState("2D/3D");
  const [_movieList, _setMovies] = useState([]);
  const [locations, setLocations] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  /* #endregion */

  /* #region  UI controllers */
  const handleTabChange = (event, number) => setTabIndex(number);
  const handleExperience = (event) => {
    setExperience(event.target.value);
    filterMovies(
      { experience: event.target.value, searchQuery },
      _movieList,
      setFilteredMovieList
    );
  };
  const handleSearchQuery = (event) => {
    setSearchQuery(event.target.value);
    filterMovies(
      { experience, searchQuery: event.target.value },
      _movieList,
      setFilteredMovieList
    );
  };
  const splashScreen = useContext(SplashContext);
  //swiper
  const pagination = {
    dynamicBullets: true,
    renderBullet: function (index, className) {
      return (
        '<span class="' +
        className +
        '" style="background-color:#E4572E;"></span>'
      );
    },
  };
  const isMob = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  SwiperCore.use([Autoplay]);
  /* #endregion */

  /* #region  Api calls and initialization */
  /**Get the movies from the api
   * @return {Promise<Movies[]>} movies
   */
  const moviesApiCall = () =>
    new Promise((resolve, reject) => {
      getMovies()
        .then((movies) => {
          let list = movies;
          dispatch(setMovies(movies.map((movie) => ({ ...movie }))));
          _setMovies(list);
          filterMovies({ experience, searchQuery }, list, setFilteredMovieList);
          resolve(movies);
        })
        .catch((error) => reject(error));
    });

  const getLocations = () => ["Nairobi"];
  //api call to retreieve cinemas
  const cinemasApiCall = () =>
    new Promise((resolve, reject) => {
      getCinemas()
        .then((cinemas) => {
          _setCinemas(cinemas);
          dispatch(setCinemas(cinemas.map((cinema) => ({ ...cinema }))));
          resolve();
        })
        .catch((error) => reject(error));
    });

  const apiCalls = () =>
    new Promise((resolve, reject) => {
      setLoading(true);
      setError(null);
      cinemasApiCall()
        .then(() => {
          setLocations(getLocations());
          moviesApiCall()
            .then(() => {
              setLoading(false);
              resolve();
            })
            .catch((error) => {
              setError("Failed to load movies.");
              setLoading(false);
              reject(error);
            });
        })
        .catch((error) => {
          setError("Failed to load cinemas.");
          setLoading(false);
          reject(error);
        });
    });

  useEffect(() => {
    splashScreen(apiCalls);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  /* #endregion */

  // Helper to check if there are movies with valid banner images
  const hasBannerMovies =
    filteredMovieList.filter(
      (movie) =>
        movie.MovieImageBig &&
        !movie.MovieImageBig.includes("https://api.admit-one.co.uk")
    ).length > 0;

  /**Props to send to the movie list component */
  const movieListProps = {
    nowShowing,
    setNowShowing,
    experience,
    handleExperience,
    movies: filteredMovieList,
    searchQuery,
    handleSearchQuery,
    showLocation: true,
    locations,
  };

  /**Props to send to the cinema list component */
  const cinemaListProps = {
    cinemas: cinemas,
    movies: _movieList,
    locations,
  };

  // Conditional rendering for cinemas or movies
  if (loading) {
    return <Skeleton variant="rectangular" animation="wave" height={"100vh"} />;
  }
  if (error && error.message === "empty_response") {
    return (
      <Stack alignItems="center" justifyContent="center" minHeight="60vh">
        <Typography color="error" variant="h5">
          Something went wrong
        </Typography>
      </Stack>
    );
  }
  if (filteredMovieList.length === 0) {
    return <NoMovies />;
  }

  return (
    <>
      <Stack paddingBottom={2}>
        {!isMob && hasBannerMovies && (
          <Swiper
            id="swiper"
            slidesPerView={"auto"}
            navigation={true}
            pagination={pagination}
            modules={[Pagination, Navigation]}
            autoplay={{ delay: 6000 }}
            className="mySwiper"
            style={{
              height: "70vh",
              width: "100%",
            }}
          >
            {filteredMovieList
              .filter(
                (movie) =>
                  movie.MovieImageBig &&
                  !movie.MovieImageBig.includes("https://api.admit-one.co.uk")
              )
              .slice(0, 3)
              .map((movie, index) => (
                <SwiperSlide
                  className="swiperSlideElement"
                  key={"img-" + movie.MovieName}
                >
                  <img
                    src={movie.MovieImageBig}
                    alt="banner"
                    className="bImg"
                  />
                  <Stack
                    sx={{
                      position: "absolute",
                      width: "100%",
                      height: "100%",
                      backgroundImage: {
                        sm: "linear-gradient(-90deg, rgba(0,0,0,1) 0, rgba(0,0,0,0.2) 60%, rgba(0,0,0,0) 100%)",
                        xs: "linear-gradient(0deg, rgba(0,0,0,1) 0, rgba(0,0,0,0.2) 60%, rgba(0,0,0,0) 100%)",
                      },
                    }}
                    direction={{ sm: "row-reverse", xs: "column-reverse" }}
                  >
                    <Stack
                      p={5}
                      height={"100%"}
                      justifyContent={{ sm: "center", xs: "end" }}
                      alignItems={{ sm: "start", xs: "center" }}
                      spacing={2}
                    >
                      <Typography
                        variant="h4"
                        color={"white"}
                        sx={{ display: "flex", alignItems: "center" }}
                        fontSize={{
                          xs: "1.5rem",
                          sm: "2.125rem",
                        }}
                      >
                        {movie.MovieName}
                      </Typography>
                      <Link
                        to={`/movie/${movie.MovieID}`}
                        style={{
                          textDecoration: "none",
                          width: "fit-content",
                        }}
                      >
                        <Button
                          variant="contained"
                          sx={{ textDecoration: "none" }}
                        >
                          Book Now
                        </Button>
                      </Link>
                      {false && (
                        <img
                          style={{
                            height: "7vh",
                            width: "fit-content",
                          }}
                          alt="logo"
                          src={logo}
                          className="objFit"
                        />
                      )}
                    </Stack>
                  </Stack>
                </SwiperSlide>
              ))}
          </Swiper>
        )}
        <Box
          sx={{
            width: {
              lg: "90%",
              xs: "95%",
            },
            px: "0.5vw",
            margin: "0 auto",
          }}
        >
          <Stack spacing={1} sx={{ pt: { sm: 3, xs: 0 } }}>
            <Stack
              direction="row"
              sx={{ justifyContent: "center" }}
              spacing={2}
            >
              <Tabs value={tabIndex} onChange={handleTabChange}>
                <Tab label="Movies" />
                <Tab label="Cinemas" />
              </Tabs>
            </Stack>
            {tabIndex === 0 && <MovieList {...movieListProps} />}
            {tabIndex === 1 && <CinemaList {...cinemaListProps} />}
          </Stack>
        </Box>
      </Stack>
    </>
  );
};
export default Home;
